import { ContactList } from "components/ui/ContactList";
import { ListTodo } from "lucide-react";
import { useTasks } from "context/TasksContext";
import { useEffect } from "react";
import { useAuth } from "context/AuthContext";
import { formatCrontabHumanReadable } from "utils/formatCrontab";
import { contactListItemClasses } from "utils/contactListItemClasses";

interface TasksListProps {
  onSelectTask: (taskId: number) => void;
  selectedTaskId: number | null;
  condoId: number;
}

export function TasksList({
  onSelectTask,
  selectedTaskId,
  condoId,
}: TasksListProps) {
  const { tasks, loading, error, refresh } = useTasks();
  const { lang } = useAuth();
  const locale = lang === "es" ? "es" : "en";

  // Fetch tasks when condo changes
  useEffect(() => {
    refresh(condoId);
  }, [condoId, refresh]);

  const transformedTasks = tasks.map((task) => {
    const isScheduled = Boolean(task.crontab);
    const cronHuman =
      isScheduled && task.crontab
        ? formatCrontabHumanReadable(task.crontab, locale)
        : "";
    return {
      id: task.id,
      name: task.title,
      isScheduled,
      subtitle: isScheduled
        ? locale === "es"
          ? `Agendada: ${cronHuman}`
          : `Scheduled: ${cronHuman}`
        : locale === "es"
          ? `Sin periodicidad • Desactivada`
          : `No schedule • Disabled`,
    };
  });

  const renderTaskItem = (
    item: (typeof transformedTasks)[0],
    isSelected: boolean
  ) => (
    <div
      className={contactListItemClasses(isSelected, {
        inactive: !item.isScheduled,
      })}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div
            className={`text-xs lg:text-sm truncate font-medium ${
              !item.isScheduled ? "text-gray-500" : ""
            }`}
          >
            {item.name}
          </div>
          <div className="text-xs text-gray-500">{item.subtitle}</div>
        </div>
      </div>
    </div>
  );

  return (
    <ContactList
      listId="tasks"
      title={locale === "es" ? "Tareas" : "Tasks"}
      icon={<ListTodo className="w-4 h-4 mr-2" />}
      items={transformedTasks}
      loading={loading}
      error={error}
      searchPlaceholder={locale === "es" ? "Buscar tareas..." : "Search tasks..."}
      selectedId={selectedTaskId}
      onSelectItem={onSelectTask}
      getItemName={(item) => item.name}
      getItemSubtitle={(item) => item.subtitle}
      countItemsForHeader={(item) => item.isScheduled}
      renderItem={renderTaskItem}
    />
  );
}
